/**
 * Custom hooks for Strategic Question Workflow Analysis
 *
 * Provides React Query hooks for:
 * - Triggering question analysis
 * - Batch analyzing all questions
 * - Fetching analysis results
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { standaloneCompaniesAPI } from '../lib/api';
import type {
  QuestionAnalysisResult,
  BatchAnalysisResult,
  AnalyzeQuestionRequest,
} from '../types/workflowAnalysis';

/**
 * Hook to trigger analysis for a single strategic question
 */
export const useAnalyzeQuestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      caseId,
      questionId,
      llmProvider,
    }: {
      caseId: string;
      questionId: string;
      llmProvider?: 'openai' | 'anthropic';
    }) => {
      // api.post() already returns the data directly, not wrapped in response.data
      const data = await standaloneCompaniesAPI.analyzeStrategicQuestion(
        caseId,
        questionId,
        llmProvider
      );
      return data as QuestionAnalysisResult;
    },
    onSuccess: (data, variables) => {
      // Invalidate question analysis cache
      queryClient.invalidateQueries({
        queryKey: ['question-analysis', variables.caseId, variables.questionId],
      });
      // Invalidate case questions cache (to update status in list)
      queryClient.invalidateQueries({
        queryKey: ['case-questions', variables.caseId],
      });
    },
  });
};

/**
 * Hook to trigger batch analysis for all questions in a case
 */
export const useAnalyzeAllQuestions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      caseId,
      llmProvider,
    }: {
      caseId: string;
      llmProvider?: 'openai' | 'anthropic';
    }) => {
      // api.post() already returns the data directly, not wrapped in response.data
      const data = await standaloneCompaniesAPI.analyzeAllStrategicQuestions(caseId, llmProvider);
      return data as BatchAnalysisResult;
    },
    onSuccess: (data, variables) => {
      // Invalidate all question analyses for this case
      queryClient.invalidateQueries({
        queryKey: ['question-analysis', variables.caseId],
      });
      // Invalidate case questions list
      queryClient.invalidateQueries({
        queryKey: ['case-questions', variables.caseId],
      });
    },
  });
};

/**
 * Hook to fetch analysis results for a specific question
 */
export const useQuestionAnalysis = (
  caseId: string,
  questionId: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['question-analysis', caseId, questionId],
    queryFn: async () => {
      // api.get() already returns the data directly, not wrapped in response.data
      const data = await standaloneCompaniesAPI.getQuestionAnalysis(caseId, questionId);
      return data as QuestionAnalysisResult;
    },
    enabled: enabled && !!caseId && !!questionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Only retry once for 404s
  });
};

/**
 * Hook to fetch analysis results for all questions in a case
 * (Batched query using individual question endpoints)
 */
export const useAllQuestionAnalyses = (
  caseId: string,
  questionIds: string[],
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['question-analyses-all', caseId],
    queryFn: async () => {
      const promises = questionIds.map(async (questionId) => {
        try {
          // api.get() already returns the data directly, not wrapped in response.data
          const data = await standaloneCompaniesAPI.getQuestionAnalysis(caseId, questionId);
          return data as QuestionAnalysisResult;
        } catch (error) {
          // Return null for questions without analysis yet
          return null;
        }
      });

      const results = await Promise.all(promises);
      return results.filter(Boolean) as QuestionAnalysisResult[];
    },
    enabled: enabled && !!caseId && questionIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Helper hook to check if all questions have been analyzed
 */
export const useAnalysisCompletionStatus = (
  caseId: string,
  totalQuestions: number
) => {
  const { data: analyses } = useAllQuestionAnalyses(
    caseId,
    [], // We'll need to pass question IDs
    false // Disable for now
  );

  const analyzed = analyses?.filter((a) => a.has_workflow_result).length || 0;
  const pending = totalQuestions - analyzed;
  const completionRate = totalQuestions > 0 ? (analyzed / totalQuestions) * 100 : 0;

  return {
    analyzed,
    pending,
    totalQuestions,
    completionRate,
    isComplete: analyzed === totalQuestions && totalQuestions > 0,
  };
};
